import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Flex, IconButton, SkeletonText, Text } from '@bonusly/design-system';

import { useBizyChatActions } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { useBizyLinkClick } from '../context/link-context';
import { useBizyUserResolver } from '../context/user-resolver-context';
import { CheckOutline, PlusOutline } from '../lib/icons';
import type { BizyNodeProps } from './node-props';

interface MeetingPerson {
	userId?: string;
	participantKey?: string;
	subtitle?: string | null;
	cta?: { label?: string; href?: string };
	status?: string | null;
	name?: string;
	username?: string;
	avatarUrl?: string | null;
}

type MeetingPeopleCardPayload = {
	title?: string;
	people?: MeetingPerson[];
	cardSurface?: string;
	category?: string | null;
};

/**
 * Vertical stack of compact per-person rows (the `suggest_people_to_meet`
 * surface). Each row resolves its display name + avatar through the injected
 * user resolver and navigates via the host link handler.
 */
export function MeetingPeopleCardNode({ node, cardIndex }: BizyNodeProps) {
	const { track } = useBizyAnalytics();
	const { messageKey, nodeChoices, onChoiceUpdate, isReadOnly } = useBizyChatActions();
	const { title, people = [], cardSurface, category } = (node as { payload: MeetingPeopleCardPayload }).payload;
	const externalChoice = nodeChoices?.[cardIndex];

	const [completedLocally, setCompletedLocally] = useState<Record<number, boolean>>({});
	const hasRecordedChoiceRef = useRef(externalChoice === 'approved');

	useEffect(() => {
		if (externalChoice === 'approved') hasRecordedChoiceRef.current = true;
	}, [externalChoice]);

	const handleRowInteract = useCallback(
		(index: number) => {
			if (isReadOnly) return;
			setCompletedLocally((prev) => ({ ...prev, [index]: true }));
			if (!hasRecordedChoiceRef.current && messageKey && onChoiceUpdate) {
				hasRecordedChoiceRef.current = true;
				onChoiceUpdate(messageKey, cardIndex, 'approved');
			}
			const person = people[index];
			track(BizyAnalyticsEvent.MeetingPeopleCardClicked, {
				cardSurface: cardSurface ?? 'chat_tool',
				category: category ?? null,
				participantKey: person?.participantKey ?? null,
				userId: person?.userId ?? null,
			});
		},
		[isReadOnly, messageKey, onChoiceUpdate, cardIndex, people, cardSurface, category, track]
	);

	if (!Array.isArray(people) || people.length === 0) return null;

	return (
		<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Box my={2}>
				{title && (
					<Text fontSize="xs" fontWeight="semibold" color="text.softer" textTransform="uppercase" letterSpacing="wider" mb={2}>
						{title}
					</Text>
				)}

				<Flex direction="column" gap={2}>
					{people.map((person, index) => {
						const isCompleted = person.status === 'completed' || completedLocally[index] === true;
						return (
							<PersonRow
								key={`${person.participantKey ?? person.userId ?? index}`}
								person={person}
								isCompleted={isCompleted}
								isReadOnly={isReadOnly}
								completedAriaLabelKey="meeting_people_card.completed_aria_label"
								createAriaLabelKey="meeting_people_card.create_aria_label"
								onInteract={() => handleRowInteract(index)}
							/>
						);
					})}
				</Flex>
			</Box>
		</motion.div>
	);
}

function PersonRow({
	person,
	isCompleted,
	isReadOnly,
	completedAriaLabelKey,
	createAriaLabelKey,
	onInteract,
}: {
	person: MeetingPerson;
	isCompleted: boolean;
	isReadOnly: boolean;
	completedAriaLabelKey: string;
	createAriaLabelKey: string;
	onInteract: () => void;
}) {
	const { t } = useTranslation('admin_bizy');
	const onLinkClick = useBizyLinkClick();
	const resolveUser = useBizyUserResolver();
	const resolved = resolveUser(person.userId);

	const displayName = resolved.displayName ?? person.name ?? person.username ?? '';
	const avatarSrc = resolved.avatarUrl ?? person.avatarUrl ?? undefined;
	const nameForAria = displayName || person.userId || '';
	const isLoadingThisRow = resolved.isLoading && !resolved.displayName && !person.name && !person.username;
	const href = person.cta?.href;

	const completedAriaLabel = t(completedAriaLabelKey, { name: nameForAria });
	const createAriaLabel = t(createAriaLabelKey, { name: nameForAria });

	const activate = () => {
		onInteract();
		if (href) onLinkClick(href);
	};

	// Inert row — no navigable CTA, or a read-only view (super-admin history,
	// shared transcripts) where the row is a static preview and must not
	// navigate on the viewer's behalf. Render the shell without click behavior.
	if (!href || isReadOnly) {
		return (
			<RowShell
				displayName={displayName}
				avatarSrc={avatarSrc}
				subtitle={person.subtitle ?? null}
				isCompleted={isCompleted}
				isLoadingDisplayData={isLoadingThisRow}
				showHoverBackground={false}
			>
				<IconButton
					aria-label={isCompleted ? completedAriaLabel : createAriaLabel}
					icon={isCompleted ? <CheckOutline boxSize={4} color="success.text" /> : <PlusOutline boxSize={4} />}
					size="sm"
					variant="ghost"
					colorScheme={isCompleted ? 'success' : 'primary'}
					isDisabled
					flexShrink={0}
					pointerEvents="none"
				/>
			</RowShell>
		);
	}

	return (
		<Box
			as="button"
			type="button"
			display="block"
			width="100%"
			textAlign="left"
			textDecoration="none"
			_hover={{ textDecoration: 'none' }}
			aria-label={isCompleted ? completedAriaLabel : createAriaLabel}
			onClick={activate}
		>
			<RowShell
				displayName={displayName}
				avatarSrc={avatarSrc}
				subtitle={person.subtitle ?? null}
				isCompleted={isCompleted}
				isLoadingDisplayData={isLoadingThisRow}
				showHoverBackground={!isCompleted}
			>
				<RowAffordanceIcon icon={isCompleted ? <CheckOutline boxSize={4} color="success.text" /> : <PlusOutline boxSize={4} />} />
			</RowShell>
		</Box>
	);
}

function RowAffordanceIcon({ icon }: { icon: ReactNode }) {
	return (
		<Flex as="span" aria-hidden="true" align="center" justify="center" boxSize={8} flexShrink={0} color="inherit">
			{icon}
		</Flex>
	);
}

function RowShell({
	displayName,
	avatarSrc,
	subtitle,
	isCompleted,
	isLoadingDisplayData,
	showHoverBackground,
	children,
}: {
	displayName: string;
	avatarSrc: string | undefined;
	subtitle: string | null;
	isCompleted: boolean;
	isLoadingDisplayData: boolean;
	showHoverBackground: boolean;
	children: ReactNode;
}) {
	const rowState = isCompleted ? 'completed' : showHoverBackground ? 'interactive' : 'inert';
	return (
		<Box
			data-state={rowState}
			border="1px solid"
			borderColor={isCompleted ? 'success.border' : 'border.soft'}
			bg={isCompleted ? 'success.bg' : 'bg.surface'}
			borderRadius={12}
			px={3}
			py={2.5}
			transition="background 0.15s, border-color 0.15s"
			_hover={showHoverBackground ? { bg: 'bg.subtle' } : undefined}
		>
			<Flex align="center" gap={3}>
				<Avatar name={displayName} src={avatarSrc} size="sm" flexShrink={0} />

				<Flex direction="column" flex="1 1 auto" minW={0} gap={0.5}>
					{isLoadingDisplayData ? (
						<SkeletonText noOfLines={1} skeletonHeight="3" width="60%" />
					) : (
						<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1}>
							{displayName}
						</Text>
					)}
					{subtitle && (
						<Text fontSize="xs" color="text.soft" noOfLines={3} lineHeight="short">
							{subtitle}
						</Text>
					)}
				</Flex>

				{children}
			</Flex>
		</Box>
	);
}
